@if (isLoading()) {
  <div class="flex flex-col gap-6">
    <vx-skeleton class="h-12 w-80" />
    <vx-skeleton class="h-40 w-full" />
    <vx-skeleton class="h-64 w-full" />
  </div>
} @else if (source(); as source) {
  <div class="flex flex-col gap-6">
    <div class="flex min-w-0 items-center gap-4">
      @if (type(); as type) {
        <span
          class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-zinc-800">
          <img class="size-7" [src]="type.icon" [alt]="type.label" />
        </span>
      }
      <div class="flex min-w-0 flex-col gap-1">
        <h1
          class="truncate font-brand text-3xl font-normal tracking-tight text-zinc-50">
          {{ source.name }}
        </h1>
        <p
          class="flex flex-wrap items-center gap-x-2 text-sm tracking-tight text-zinc-400">
          <span>{{ type()?.label }}</span>
          @if (source.processor; as processor) {
            <span aria-hidden="true">·</span>
            <span>
              {{
                'pages.settings.alerting.sources.details.processorLabel'
                  | transloco
              }}
              <a
                [routerLink]="['/settings/alerting/processors', processor.id]"
                class="rounded-sm text-zinc-200 underline-offset-2 hover:text-zinc-50 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-50">
                {{ processor.name }}
              </a>
            </span>
          }
        </p>
      </div>
    </div>

    @if (isManaged()) {
      <vx-callout
        tone="info"
        icon="info"
        [title]="
          'pages.settings.alerting.sources.details.managedHeader' | transloco
        ">
        <p class="whitespace-pre-line">
          {{
            'pages.settings.alerting.sources.details.inciditeAlertSourceDescription'
              | transloco
          }}
        </p>
      </vx-callout>
    } @else {
      <vx-card>
        <vx-card-header
          [title]="
            'pages.settings.alerting.sources.details.webhook.header' | transloco
          "
          [description]="
            'pages.settings.alerting.sources.details.webhook.description'
              | transloco
          " />
        <vx-panel variant="group">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium tracking-tight text-zinc-300">
                {{
                  'pages.settings.alerting.sources.details.webhookEndpoint'
                    | transloco
                }}
              </span>
              <app-copy-value
                [value]="source.webhookEndpoint"
                [copyLabel]="
                  'pages.settings.alerting.sources.details.webhook.copyEndpoint'
                    | transloco
                " />
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium tracking-tight text-zinc-300">
                {{
                  'pages.settings.alerting.sources.details.webhook.secretLabel'
                    | transloco
                }}
              </span>
              <app-copy-value
                [value]="source.token"
                [secret]="true"
                [copyLabel]="
                  'pages.settings.alerting.sources.details.webhook.copySecret'
                    | transloco
                "
                [revealLabel]="
                  'pages.settings.alerting.sources.details.webhook.revealSecret'
                    | transloco
                "
                [hideLabel]="
                  'pages.settings.alerting.sources.details.webhook.hideSecret'
                    | transloco
                " />
            </div>
          </div>
        </vx-panel>
      </vx-card>
    }

    <section class="flex flex-col gap-4">
      <div class="flex min-w-0 flex-col gap-1">
        <h2 class="text-xl font-medium tracking-tight text-zinc-50">
          {{
            'pages.settings.alerting.sources.details.relatedAlerts' | transloco
          }}
        </h2>
        <p class="text-sm tracking-tight text-zinc-400">
          {{
            'pages.settings.alerting.sources.details.alertsDescription'
              | transloco
          }}
        </p>
      </div>

      <vx-data-table
        [columns]="columns"
        [rows]="rows()"
        [rowKey]="rowKey"
        [rowLink]="alertLink"
        [loading]="alertsLoading()"
        [page]="{ index: page(), size: pageSize() }"
        [total]="totalRecords()"
        [pageSizeOptions]="[5, 10, 20]"
        (pageChanged)="onPageChanged($event)"
        emptyIcon="warning"
        [emptyTitle]="
          'pages.settings.alerting.sources.details.noAlerts' | transloco
        "
        [rangeLabel]="rangeLabel()"
        [previousLabel]="'actions.previous' | transloco"
        [nextLabel]="'actions.next' | transloco"
        [pageSizeLabel]="'components.pagination.pageSize' | transloco">
        <ng-template vxHeader="alert">{{
          'resources.alert.singular' | transloco
        }}</ng-template>
        <ng-template vxHeader="status">{{
          'resources.alert.status.label' | transloco
        }}</ng-template>
        <ng-template vxHeader="processing">{{
          'pages.settings.alerting.sources.details.processingColumn' | transloco
        }}</ng-template>
        <ng-template vxHeader="started">{{
          'resources.alert.startedAt' | transloco
        }}</ng-template>
        <ng-template vxCell="alert" [vxCellOf]="rows()" let-row>
          <vx-item-cell
            icon="warning"
            [title]="row.alert.name"
            [subtitle]="row.alert.description" />
        </ng-template>

        <ng-template vxCell="status" [vxCellOf]="rows()" let-row>
          <app-alert-status-label [state]="row.alert.state" />
        </ng-template>

        <ng-template vxCell="processing" [vxCellOf]="rows()" let-row>
          <span class="text-sm tracking-tight text-zinc-400">
            @switch (row.processing) {
              @case ('processed') {
                <span
                  [vxTooltip]="
                    'pages.settings.alerting.sources.details.processedAt'
                      | transloco: { processedAt: row.processedAt | dateAgo }
                  ">
                  <vx-status-dot tone="success">
                    {{
                      'pages.settings.alerting.sources.details.processed'
                        | transloco
                    }}
                  </vx-status-dot>
                </span>
              }
              @case ('failed') {
                <vx-status-dot tone="danger">
                  {{
                    'pages.settings.alerting.sources.details.processingFailed'
                      | transloco
                  }}
                </vx-status-dot>
              }
              @default {
                <vx-status-dot tone="neutral">
                  {{
                    'pages.settings.alerting.sources.details.pending'
                      | transloco
                  }}
                </vx-status-dot>
              }
            }
          </span>
        </ng-template>

        <ng-template vxCell="started" [vxCellOf]="rows()" let-row>
          <span class="text-sm tracking-tight text-zinc-400">
            {{ row.alert.startedAt | dateAgo }}
          </span>
        </ng-template>

        <ng-template vxRowActions [vxRowActionsOf]="rows()" let-row>
          <vx-icon-button
            icon="dots-three"
            variant="ghost"
            size="sm"
            tone="secondary"
            [ariaLabel]="'pages.alertDetails.actions.menu' | transloco"
            [vxMenuTriggerFor]="rowMenu" />
          <ng-template #rowMenu>
            <vx-menu>
              <vx-menu-item icon="eye" (triggered)="openAlert(row)">
                {{ 'pages.alertDetails.actions.openAlert' | transloco }}
              </vx-menu-item>
            </vx-menu>
          </ng-template>
        </ng-template>
      </vx-data-table>
    </section>

    @if (source.isDeletable) {
      <vx-card>
        <vx-card-header
          [title]="'components.dangerZone.header' | transloco"
          [description]="
            'pages.settings.alerting.sources.details.dangerZone.description'
              | transloco
          ">
          <div vxCardHeaderActions [vRequiredRoles]="['alertsources.manager']">
            <vx-button tone="danger" (click)="deleteSource()">
              <vx-icon name="trash" size="sm" />
              {{
                'pages.settings.alerting.sources.details.dangerZone.deleteBtn'
                  | transloco
              }}
            </vx-button>
          </div>
        </vx-card-header>
      </vx-card>
    }
  </div>
}
